import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import api from "@/lib/api";
import { Award, LogOut, Mail, Phone, MapPin, Globe } from "lucide-react";

const TIER_COLOR = {
  Bronze: "#CD7F32", Silver: "#94A3B8", Gold: "#EAB308", Platinum: "#7E22CE",
};

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const { t, lang } = useLang();
  const { formatPrice, currency, setCurrency } = useCurrency();
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    api.get("/states").then(r => setStates(r.data));
    api.get("/currencies").then(r => setCurrencies(r.data));
  }, []);

  const updateState = async (key) => {
    const { data } = await api.patch("/me/state", { state: key });
    setUser(data);
  };
  const updateCurrency = async (code) => {
    setCurrency(code);
    try { const { data } = await api.patch("/me/currency", { currency: code }); setUser(data); } catch (_e) { /* allow even if not logged */ }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const userState = states.find(s => s.key === user?.state);

  return (
    <CustomerLayout>
      <div className="max-w-xl mx-auto px-4 py-6">
        <div className="so-card p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[var(--so-orange)] text-white text-3xl font-bold flex items-center justify-center mx-auto">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="mt-3 font-bold text-xl" data-testid="profile-name">{user?.name}</div>
          <div className="text-sm text-[var(--so-text-soft)]">{user?.role}</div>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${TIER_COLOR[user?.loyalty_tier]}20`, color: TIER_COLOR[user?.loyalty_tier] }} data-testid="profile-tier">
            <Award className="w-3.5 h-3.5" /> {user?.loyalty_tier}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="so-card p-4">
            <div className="text-xs text-[var(--so-text-soft)] uppercase tracking-wider">{t("loyaltyPoints")}</div>
            <div className="text-2xl font-bold mt-1" data-testid="profile-points">{user?.loyalty_points}</div>
          </div>
          <div className="so-card p-4">
            <div className="text-xs text-[var(--so-text-soft)] uppercase tracking-wider">{t("wallet")}</div>
            <div className="text-2xl font-bold mt-1 text-[var(--so-orange)]">{formatPrice(user?.wallet_balance)}</div>
          </div>
        </div>

        <div className="mt-5 so-card p-5 space-y-4 text-sm">
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-[var(--so-text-soft)]" />
            <span>{user?.email}</span>
          </div>
          {user?.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-[var(--so-text-soft)]" />
              <span>{user?.phone}</span>
            </div>
          )}
          <div className="flex items-center gap-3">
            <MapPin className="w-4 h-4 text-[var(--so-text-soft)] flex-shrink-0" />
            <select value={user?.state || ""} onChange={(e) => updateState(e.target.value)} className="so-input !py-1.5 text-sm" data-testid="profile-state">
              {states.map(s => (
                <option key={s.key} value={s.key} disabled={s.status !== "live"}>
                  {lang === "ar" ? s.name_ar : s.name_en} {s.status !== "live" ? "(coming soon)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <Globe className="w-4 h-4 text-[var(--so-text-soft)] flex-shrink-0" />
            <select value={currency} onChange={(e) => updateCurrency(e.target.value)} className="so-input !py-1.5 text-sm" data-testid="profile-currency">
              {currencies.map(c => (
                <option key={c.code} value={c.code}>{c.code} — {lang === "ar" ? c.name_ar : c.name_en}</option>
              ))}
            </select>
          </div>
        </div>

        <button onClick={handleLogout} className="so-btn-secondary w-full justify-center mt-5" data-testid="profile-logout">
          <LogOut className="w-4 h-4" /> {t("logout")}
        </button>
      </div>
    </CustomerLayout>
  );
}
